"use client";

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl"


/**
 * Searchable IANA timezone picker. The full zone list comes from
 * `Intl.supportedValuesOf('timeZone')` (modern runtimes); a curated subset is
 * the fallback for the rare environment that lacks it. The current value is
 * always included even if absent from the list, so a previously-saved zone
 * always renders. Shared by the calendar working-hours editor across voice /
 * email / messaging agents.
 */

// Fallback list when `Intl.supportedValuesOf` is unavailable.
const FALLBACK_TIMEZONES = [
  "UTC",
  "Europe/Kiev",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Warsaw",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Australia/Sydney",
];

function listTimezones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("timeZone");
    if (supported && supported.length > 0) return supported;
  } catch {
    /* fall through to the curated list */
  }
  return FALLBACK_TIMEZONES;
}

/** Short GMT offset (e.g. "GMT+3") for the zone, or "" if it can't be derived. */
function getOffsetLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

function formatTimezoneLabel(tz: string): string {
  if (!tz) return "";
  const pretty = tz.replace(/_/g, " ");
  const offset = getOffsetLabel(tz);
  return offset ? `${pretty} (${offset})` : pretty;
}

interface TimezoneComboboxProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
}

export function TimezoneCombobox({
  value,
  onChange,
  disabled,
}: TimezoneComboboxProps) {
  const tUi = useTranslations("AgentTools.AgentsSharedCalendarTimezoneCombobox")

  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    const zones = listTimezones();
    if (value && !zones.includes(value)) zones.unshift(value);
    return zones.map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }));
  }, [value]);

  const selectedLabel = useMemo(() => formatTimezoneLabel(value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span key={value ? selectedLabel : tUi("selectTimezone")} className="truncate">
            {value ? selectedLabel : tUi("selectTimezone")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={tUi("searchTimezone")} />
          <CommandList>
            <CommandEmpty><span key={tUi("noTimezoneFound")}>{tUi("noTimezoneFound")}</span></CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
